import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

export interface Attachment {
  fileName: string;
  fileType: string;
  url: string;
}

export type ProductionSchedule = {
  key: string;
  productionQuantity: number;
  plannedStartDate: string;
  productionLeadDays: number;
  plannedEndDate: string;
};
export type ShipmentSchedule = {
  key: string;
  shippingQuantity: number;
  shippingDate: string;
  shippingLeadDays: number;
  shippingEndDate: string;
};

export interface MaterialOrService {
  lineNo: number;
  itemType: "Material" | "Service";
  category: string;
  subCategory?: string;
  materialName?: string;
  workLineName?: string;
  quantity: number;
  uom: string;
  uomName?: string;
  unitPrice: number;
  availableStock?: number;
  needByDate: string;
  billingAddress: string;
  deliveryAddress: string;
  description: string;
  attachments: Attachment[];
  isInstallationRequired?: boolean;
  source?: string;
  pricingInfo?: {
    currency?: string;
    supplyQuantity?: number;
    taxType: "GST" | "VAT";
    isUnitPriceNegotiable?: any;
    unitPrice: number;
    taxPercentage: number;
    workingDays?: number;
    installationCharges?: number;
    discount?: number;
  };
  planningSchedules?: {
    startDate: string;
    plannedLeadDays: number;
    endDate: string;
  };
  productionSchedules?: ProductionSchedule[]; //Production Quantity Should not be more than Supply Quantity
  shipmentSchedules?: ShipmentSchedule[]; //Shipping Quantity Should not be more than Supply Quantity
}

export interface Supplier {
  supplierID: string;
  supplierName: string;
}

export interface ApprovalWorkflow {
  checker: {
    name: string;
    role: string;
  };
  verifier: {
    name: string;
    role: string;
  };
  approver: {
    name: string;
    role: string;
  };
}

export interface DocumentSettings {
  documentsFromSuppliersRequired: boolean;
  termsAndConditions: string[];
}

export interface SiteInfo {
  projectName: string;
  siteCode: string;
}

export interface Question {
  description: string;
  type:
    | "Yes/No"
    | "Long Text"
    | "Short Text"
    | "Single Choice"
    | "Multi Choice"
    | "File Upload";
  options?: string[];
  mandatory: boolean;
  weightage: number;
  answer?: any;
  remarks?: string;
}

export interface QuestionnaireSection {
  sectionName: string;
  totalWeightage: number;
  questions: Question[];
  //   approvals: ApprovalWorkflow;
}

export interface Tender {
  tenderID: string;
  tenderTitle: string;
  customerName: string;
  requestedBy: string;
  paymentTerms: string;
  isNegotiable?: boolean;
  validateDate?: any;
  tenderType: "Open" | "Restricted";
  category: "Materials" | "Services";
  status:
    | "New Tender"
    | "Interested"
    | "Not Interested"
    | "Responded"
    | "Negotiation"
    | "Partially Awarded"
    | "Awarded"
    | "Cancelled";
  notInterestedReason?: string;
  site: SiteInfo;
  submissionDeadline: string;
  expressionOfInterestDeadline: string;
  description: string;
  negotiable?: boolean;
  materialsOrServices: MaterialOrService[];
  deliverySettings: {
    isLineLevelNeedByDate: boolean;
    isLineLevelDeliveryAddress: boolean;
  };
  needByDate?: string;
  Questionnaires: QuestionnaireSection[];
  customerTermsAndConditions: string[];
  supplierTermsAndConditions: string[];
}

export type TenderState = {
  tenders: Tender[];
  loading: boolean;
  error: string | null;
};

export const tenders: Tender[] = [
  {
    tenderID: "MCMC-2025-MAT-001",
    tenderTitle: "Supply of Network Infrastructure Materials",
    customerName: "MCMC National Fiberization Project",
    requestedBy: "Ahmad Faizal",
    paymentTerms: "Pay on Delivery",
    isNegotiable: true,
    validateDate: "2025-08-09T18:30:00.000Z",
    tenderType: "Open",
    category: "Materials",
    status: "Responded",
    site: {
      projectName: "NFCP Phase 4 - Northern Region",
      siteCode: "MCMC-NFCP-04",
    },
    submissionDeadline: "2025-08-10",
    expressionOfInterestDeadline: "2025-07-25",
    description:
      "Tender for supply of fiber cables, network components and related materials for NFCP Phase 4 deployment in northern Malaysia.",
    materialsOrServices: [
      {
        lineNo: 1,
        itemType: "Material",
        category: "Fiber Optic",
        materialName: "Single Mode Fiber Cable",
        quantity: 1000,
        unitPrice: 380,
        uom: "Rolls",
        availableStock: 500,
        needByDate: "2025-08-15",
        billingAddress: "MCMC Procurement HQ, Cyberjaya",
        deliveryAddress: "MCMC Northern Warehouse, Penang",
        description: "ITU-T G.652D standard compliant fiber optic cable",
        attachments: [
          {
            fileName: "fiber_specifications.pdf",
            fileType: "application/pdf",
            url: "https://mcmc.gov.my/files/fiber_specifications.pdf",
          },
        ],
        source: "Registered Supplier Panel",
        pricingInfo: {
          supplyQuantity: 10,
          isUnitPriceNegotiable: "No",
          unitPrice: 380,
          taxPercentage: 10,
          taxType: "GST",
        },
        planningSchedules: {
          endDate: "2025-07-24T18:30:00.000Z",
          plannedLeadDays: 5,
          startDate: "2025-07-19T18:30:00.000Z",
        },
        productionSchedules: [
          {
            key: "new-1752927524540",
            productionQuantity: 20,
            productionLeadDays: 10,
            plannedStartDate: "01/Jul/2025",
            plannedEndDate: "",
          },
        ],
        shipmentSchedules: [
          {
            key: "new-1752927539916",
            shippingQuantity: 50,
            shippingLeadDays: 30,
            shippingDate: "30/Jul/2025",
            shippingEndDate: "",
          },
        ],
      },
      {
        lineNo: 2,
        itemType: "Material",
        category: "Network Equipment",
        materialName: "Outdoor Cabinets",
        quantity: 250,
        unitPrice: 1200,
        uom: "Units",
        availableStock: 100,
        needByDate: "2025-08-20",
        billingAddress: "MCMC Procurement HQ, Cyberjaya",
        deliveryAddress: "NFCP Site Depot, Kedah",
        description: "Weatherproof enclosures for fiber terminations",
        attachments: [
          {
            fileName: "cabinet_specs.pdf",
            fileType: "application/pdf",
            url: "https://mcmc.gov.my/files/cabinet_specs.pdf",
          },
        ],
        source: "Registered Supplier Panel",
        pricingInfo: {
          supplyQuantity: 30,
          isUnitPriceNegotiable: "No",
          unitPrice: 1200,
          taxPercentage: 10,
          taxType: "VAT",
        },
        planningSchedules: {
          endDate: "2025-08-01T18:30:00.000Z",
          plannedLeadDays: 6,
          startDate: "2025-07-26T18:30:00.000Z",
        },
        productionSchedules: [
          {
            key: "new-1752927585464",
            productionQuantity: 8,
            productionLeadDays: 10,
            plannedStartDate: "10/Jul/2025",
            plannedEndDate: "",
          },
        ],
        shipmentSchedules: [
          {
            key: "new-1752927603606",
            shippingQuantity: 30,
            shippingLeadDays: 10,
            shippingDate: "20/Jul/2025",
            shippingEndDate: "",
          },
        ],
      },
    ],
    deliverySettings: {
      isLineLevelNeedByDate: true,
      isLineLevelDeliveryAddress: true,
    },
    Questionnaires: [
      {
        sectionName: "General",
        totalWeightage: 25,
        questions: [
          {
            description: "Do you comply with SIRIM and MCMC standards?",
            type: "Yes/No",
            mandatory: true,
            weightage: 8,
            answer: "Yes",
            remarks: "remarks ",
          },
          {
            description: "Provide company registration number and details.",
            type: "Short Text",
            mandatory: true,
            weightage: 7,
            answer: "REG-2025-001",
            remarks: "",
          },
          {
            description: "Describe your quality and safety procedures.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
            answer: "some des",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Commercial",
        totalWeightage: 25,
        questions: [
          {
            description: "Are you able to meet the proposed payment terms?",
            type: "Yes/No",
            mandatory: true,
            weightage: 10,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Provide your pricing breakdown methodology.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
            answer: "Cost-plus pricing with transparent margin structure",
            remarks: "",
          },
          {
            description: "Do you offer warranty on supplied materials?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["1 Year", "2 Years", "3 Years", "No Warranty"],
            answer: "2 Years",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Technical",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you have ISO 9001 certification?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Describe your technical capabilities for fiber optic materials.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "Full range of fiber optic supply and testing capabilities",
            remarks: "",
          },
          {
            description: "How many similar projects have you completed?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["1-5", "6-10", "11-20", "More than 20"],
            answer: "6-10",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Legal",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you accept the standard terms and conditions?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Are there any pending legal disputes against your company?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "No",
            remarks: "",
          },
          {
            description: "Provide details of your insurance coverage.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "Comprehensive liability insurance up to RM 5 million",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Documents",
        totalWeightage: 20,
        questions: [
          {
            description: "Upload SIRIM certification document.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
            answer: {},
            remarks: "no such files ",
          },
          {
            description: "Upload company registration certificate (SSM).",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
            answer: {},
            remarks: "",
          },
          {
            description: "Upload financial statements for the last 3 years.",
            type: "File Upload",
            mandatory: true,
            weightage: 6,
            answer: {},
            remarks: "",
          },
        ],
      },
    ],
    customerTermsAndConditions: [
      "No advance payment",
      "Timely delivery as per site milestone",
    ],
    supplierTermsAndConditions: [
      "Full payment within 30 days of invoice receipt",
      "Late delivery will incur penalties per MCMC policy",
    ],
    negotiable: true,
  },
  {
    tenderID: "MCMC-2025-SRV-002",
    customerName: "MCMC Regional Office",
    requestedBy: "Nur Aisyah",
    paymentTerms: "Net 30 days",
    isNegotiable: true,
    validateDate: "2025-08-14T18:30:00.000Z",
    tenderTitle: "Maintenance of Broadcast Equipment",
    tenderType: "Restricted",
    category: "Services",
    status: "Negotiation",
    site: {
      projectName: "TV & Radio Tower Upgrade",
      siteCode: "MCMC-BCAST-09",
    },
    submissionDeadline: "2025-09-05",
    expressionOfInterestDeadline: "2025-08-20",
    description:
      "Tender for preventive and corrective maintenance of MCMC broadcasting equipment at hill stations and regional towers.",
    materialsOrServices: [
      {
        lineNo: 3,
        itemType: "Service",
        category: "Equipment Maintenance",
        subCategory: "Transmitter & Receiver",
        workLineName: "VHF/UHF Transmitters",
        quantity: 150,
        uom: "Hours",
        unitPrice: 150,
        needByDate: "2025-09-10",
        billingAddress: "MCMC Tower Ops Office, Cyberjaya",
        deliveryAddress: "Gunung Jerai Broadcast Site, Kedah",
        description:
          "Monthly servicing and fault rectification of radio transmission systems",
        attachments: [],
        pricingInfo: {
          unitPrice: 150,
          workingDays: 50,
          taxPercentage: 20,
          taxType: "VAT",
        },
      },
      {
        lineNo: 4,
        itemType: "Service",
        category: "Audit & Testing",
        subCategory: "Electrical Safety",
        workLineName: "Grounding Systems",
        quantity: 10,
        unitPrice: 500,
        uom: "Audits",
        needByDate: "2025-09-15",
        billingAddress: "MCMC Tower Ops Office, Cyberjaya",
        deliveryAddress: "Bukit Larut Transmission Site, Perak",
        description:
          "Inspection and compliance audit of electrical grounding systems",
        attachments: [],
        pricingInfo: {
          unitPrice: 500,
          workingDays: 50,
          taxPercentage: 10,
          taxType: "GST",
        },
      },
    ],
    deliverySettings: {
      isLineLevelNeedByDate: false,
      isLineLevelDeliveryAddress: false,
    },
    Questionnaires: [
      {
        sectionName: "General",
        totalWeightage: 25,
        questions: [
          {
            description:
              "Company registration with Suruhanjaya Syarikat Malaysia (SSM)",
            type: "Short Text",
            mandatory: true,
            weightage: 8,
            answer: "resposne",
            remarks: "few remarks ",
          },
          {
            description: "Select your business entity type",
            type: "Multi Choice",
            mandatory: true,
            weightage: 7,
            options: ["Sdn. Bhd.", "Berhad", "Enterprise", "Sole Proprietor"],
            answer: ["Berhad", "Enterprise"],
            remarks: "",
          },
          {
            description: "Have you previously served MCMC projects?",
            type: "Single Choice",
            mandatory: false,
            weightage: 10,
            options: ["Yes", "No"],
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Commercial",
        totalWeightage: 25,
        questions: [
          {
            description: "Are you able to meet the proposed payment terms (Net 30 days)?",
            type: "Yes/No",
            mandatory: true,
            weightage: 10,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Provide your service rate card and pricing structure.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
            answer: "Standard rate card based on CIDB guidelines",
            remarks: "",
          },
          {
            description: "Do you provide performance bonds or guarantees?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["Yes - Bank Guarantee", "Yes - Insurance Bond", "No"],
            answer: "Yes - Bank Guarantee",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Technical",
        totalWeightage: 15,
        questions: [
          {
            description:
              "Are your engineers certified by CIDB or relevant bodies?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "Yes",
            remarks: "no such remarks",
          },
          {
            description: "Provide SOPs for tower site safety compliance.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "few points",
            remarks: "",
          },
          {
            description: "How many years of experience in broadcast equipment maintenance?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["1-3 Years", "4-6 Years", "7-10 Years", "More than 10 Years"],
            answer: "7-10 Years",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Legal",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you accept the standard terms and conditions?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Are there any pending legal disputes against your company?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "No",
            remarks: "",
          },
          {
            description: "Provide details of professional indemnity insurance.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "Professional indemnity coverage of RM 2 million",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Documents",
        totalWeightage: 20,
        questions: [
          {
            description: "Upload certification documents (CIDB/relevant bodies).",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
            answer: {},
            remarks: "",
          },
          {
            description: "Upload company profile and portfolio.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
            answer: {},
            remarks: "",
          },
          {
            description: "Upload safety compliance certificates.",
            type: "File Upload",
            mandatory: true,
            weightage: 6,
            answer: {},
            remarks: "",
          },
        ],
      },
    ],
    customerTermsAndConditions: [
      "Service delivery must meet agreed SLA timelines",
    ],
    supplierTermsAndConditions: [
      "Weekly maintenance logs must be submitted for review",
    ],
    negotiable: true,
  },
  {
    tenderID: "MCMC-2025-SFT-003",
    tenderTitle: "Development of Network Monitoring Software",
    customerName: "MCMC HQ - Cybersecurity Division",
    requestedBy: "Tan Sri Amirul",
    paymentTerms: "40% upfront, 60% upon delivery",
    isNegotiable: false,
    validateDate: "2025-08-29T18:30:00.000Z",
    tenderType: "Open",
    category: "Services",
    status: "Partially Awarded",
    site: {
      projectName: "National Network Monitoring System (NNMS)",
      siteCode: "MCMC-NNMS-01",
    },
    submissionDeadline: "2025-09-10",
    expressionOfInterestDeadline: "2025-08-25",
    description:
      "Development and deployment of a centralized network monitoring and alert system across nationwide ISPs.",
    materialsOrServices: [
      {
        lineNo: 5,
        itemType: "Service",
        category: "Software Development",
        subCategory: "Web-based Systems",
        workLineName: "Backend & UI System",
        quantity: 1,
        uom: "Lot",
        unitPrice: 850000,
        needByDate: "2025-12-15",
        billingAddress: "MCMC IT Division, Cyberjaya",
        deliveryAddress: "MCMC Data Center, Cyberjaya",
        description:
          "Design, develop, and deploy a full-stack monitoring application integrated with SNMP and API feeds.",
        attachments: [],
        pricingInfo: {
          unitPrice: 850000,
          workingDays: 55,
          taxPercentage: 10,
          taxType: "VAT",
        },
      },
    ],
    deliverySettings: {
      isLineLevelNeedByDate: false,
      isLineLevelDeliveryAddress: false,
    },
    Questionnaires: [
      {
        sectionName: "General",
        totalWeightage: 25,
        questions: [
          {
            description: "Have you built real-time monitoring systems before?",
            type: "Yes/No",
            mandatory: true,
            weightage: 8,
            answer: "No",
            remarks: "r1",
          },
          {
            description: "Provide company registration and background details.",
            type: "Short Text",
            mandatory: true,
            weightage: 7,
            answer: "Registered under SSM since 2015",
            remarks: "",
          },
          {
            description: "Describe your organization structure and key personnel.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
            answer: "Team of 50+ developers with dedicated QA and DevOps",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Commercial",
        totalWeightage: 25,
        questions: [
          {
            description: "Are you able to meet the proposed payment terms (40% upfront, 60% upon delivery)?",
            type: "Yes/No",
            mandatory: true,
            weightage: 10,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Provide your project cost estimation methodology.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
            answer: "Agile-based estimation with sprint-level cost breakdown",
            remarks: "",
          },
          {
            description: "Do you offer post-delivery support packages?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["6 Months", "12 Months", "24 Months", "Custom"],
            answer: "12 Months",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Technical",
        totalWeightage: 15,
        questions: [
          {
            description: "Briefly explain your security practices during development.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "yes",
            remarks: "",
          },
          {
            description: "What technology stack will you use for this project?",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "React, Node.js, PostgreSQL with SNMP integration",
            remarks: "",
          },
          {
            description: "Do you follow OWASP security guidelines?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "Yes",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Legal",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you accept IP transfer and source code handover terms?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "Yes",
            remarks: "",
          },
          {
            description: "Are there any pending legal disputes against your company?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
            answer: "No",
            remarks: "",
          },
          {
            description: "Provide details of your professional indemnity insurance.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
            answer: "Professional indemnity coverage of RM 3 million",
            remarks: "",
          },
        ],
      },
      {
        sectionName: "Documents",
        totalWeightage: 20,
        questions: [
          {
            description: "Upload portfolio or case studies.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
            answer: {},
            remarks: "no doc is need",
          },
          {
            description: "Upload company registration and SSM certificate.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
            answer: {},
            remarks: "",
          },
          {
            description: "Upload financial statements for the last 3 years.",
            type: "File Upload",
            mandatory: true,
            weightage: 6,
            answer: {},
            remarks: "",
          },
        ],
      },
    ],
    customerTermsAndConditions: [
      "Source code and documentation must be delivered",
      "No subcontracting without approval",
    ],
    supplierTermsAndConditions: [
      "Payment tied to milestone acceptance",
      "Bug fixing support for 12 months",
    ],
    negotiable: true,
  },
  {
    tenderID: "MCMC-2025-INS-004",
    tenderTitle: "Supply of Structural Steel and Roofing Materials",
    customerName: "Skyline Builders Sdn Bhd",
    requestedBy: "Siti Aisyah",
    paymentTerms: "Milestone-based",
    isNegotiable: true,
    validateDate: "2025-09-15",
    tenderType: "Open",
    category: "Materials",
    status: "New Tender",
    site: {
      projectName: "KL Sentral Commercial Tower - Phase 2",
      siteCode: "SKB-KLST-04",
    },
    submissionDeadline: "2025-09-20",
    expressionOfInterestDeadline: "2025-09-05",
    description:
      "Supply of structural steel beams, columns, roofing sheets, and allied materials for the construction of a 25-storey commercial tower at KL Sentral.",
    materialsOrServices: [
      {
        lineNo: 6,
        itemType: "Material",
        category: "Structural Steel",
        materialName: "H-Beam Steel (Grade S355)",
        quantity: 500,
        unitPrice: 2800,
        uom: "Tonnes",
        availableStock: 120,
        needByDate: "2025-10-05",
        billingAddress: "Skyline Builders HQ, Petaling Jaya",
        deliveryAddress: "KL Sentral Construction Site, Kuala Lumpur",
        description: "Hot-rolled H-beam structural steel conforming to MS 1490:2019 standards for high-rise construction.",
        attachments: [],
      },
      {
        lineNo: 7,
        itemType: "Material",
        category: "Roofing",
        materialName: "Metal Deck Roofing Sheets",
        quantity: 3000,
        unitPrice: 85,
        uom: "Sqm",
        availableStock: 500,
        needByDate: "2025-10-20",
        billingAddress: "Skyline Builders HQ, Petaling Jaya",
        deliveryAddress: "KL Sentral Construction Site, Kuala Lumpur",
        description: "Galvanized corrugated metal deck roofing sheets with anti-corrosion coating for commercial building roof system.",
        attachments: [],
      },
    ],
    deliverySettings: {
      isLineLevelNeedByDate: true,
      isLineLevelDeliveryAddress: true,
    },
    Questionnaires: [
      {
        sectionName: "General",
        totalWeightage: 25,
        questions: [
          {
            description: "Have you supplied structural steel for high-rise construction projects?",
            type: "Yes/No",
            mandatory: true,
            weightage: 8,
          },
          {
            description: "Provide your company registration number and CIDB registration details.",
            type: "Short Text",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Describe your experience in supplying construction materials for projects above 10 storeys.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
          },
        ],
      },
      {
        sectionName: "Commercial",
        totalWeightage: 25,
        questions: [
          {
            description: "Are you able to meet the milestone-based payment terms?",
            type: "Yes/No",
            mandatory: true,
            weightage: 10,
          },
          {
            description: "Provide your pricing breakdown including delivery and handling charges.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
          },
          {
            description: "Do you offer defect liability warranty on supplied materials?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["6 Months", "12 Months", "24 Months", "No Warranty"],
          },
        ],
      },
      {
        sectionName: "Technical",
        totalWeightage: 15,
        questions: [
          {
            description: "Describe your quality control process for steel fabrication and material testing.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Do your materials conform to MS 1490 and BS EN 10025 standards?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "How many high-rise construction material supply projects have you completed?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["1-5", "6-10", "11-20", "More than 20"],
          },
        ],
      },
      {
        sectionName: "Legal",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you accept the standard terms and conditions including liquidated damages for late delivery?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Are there any pending legal disputes or blacklisting against your company?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Provide details of your Contractor's All Risk (CAR) insurance and material transit coverage.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
          },
        ],
      },
      {
        sectionName: "Documents",
        totalWeightage: 20,
        questions: [
          {
            description: "Upload mill test certificates and material compliance reports.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Upload CIDB registration certificate and SSM documents.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Upload past project completion certificates from previous clients.",
            type: "File Upload",
            mandatory: true,
            weightage: 6,
          },
        ],
      },
    ],
    customerTermsAndConditions: [
      "Materials must be delivered as per construction schedule within 25 working days",
      "All steel must pass third-party NDT inspection before acceptance",
    ],
    supplierTermsAndConditions: [
      "Full reporting of material test certificates upon each delivery",
      "Damaged materials during transit will be replaced at supplier cost",
    ],
  },
  {
    tenderID: "MCMC-2025-TRG-005",
    tenderTitle: "Plumbing and MEP Installation Services for Residential Complex",
    customerName: "Meridian Property Development Bhd",
    requestedBy: "Encik Zulkifli",
    paymentTerms: "Progress-based (Monthly Valuation)",
    isNegotiable: false,
    validateDate: "2025-08-31",
    tenderType: "Restricted",
    category: "Services",
    status: "Interested",
    site: {
      projectName: "Meridian Heights Condominium - Block A & B",
      siteCode: "MPD-MHC-07",
    },
    submissionDeadline: "2025-09-12",
    expressionOfInterestDeadline: "2025-08-28",
    description:
      "Sub-contract for complete plumbing, fire protection, and MEP (Mechanical, Electrical & Plumbing) installation works for a 320-unit residential condominium project in Shah Alam.",
    materialsOrServices: [
      {
        lineNo: 7,
        itemType: "Service",
        category: "MEP Works",
        subCategory: "Plumbing & Sanitary",
        workLineName: "Internal Plumbing Installation",
        quantity: 320,
        uom: "Units",
        unitPrice: 8500,
        needByDate: "2025-10-01",
        billingAddress: "Meridian Property HQ, Petaling Jaya",
        deliveryAddress: "Meridian Heights Site, Seksyen 15, Shah Alam",
        description:
          "Complete internal plumbing and sanitary installation for 320 residential units including cold water, hot water, and waste piping systems.",
        attachments: [],
      },
      {
        lineNo: 8,
        itemType: "Service",
        category: "MEP Works",
        subCategory: "Fire Protection",
        workLineName: "Fire Sprinkler System Installation",
        quantity: 1,
        uom: "Lot",
        unitPrice: 450000,
        needByDate: "2025-11-15",
        billingAddress: "Meridian Property HQ, Petaling Jaya",
        deliveryAddress: "Meridian Heights Site, Seksyen 15, Shah Alam",
        description:
          "Design, supply, and installation of wet riser and sprinkler system for Block A & B as per BOMBA requirements.",
        attachments: [],
      },
    ],
    deliverySettings: {
      isLineLevelNeedByDate: false,
      isLineLevelDeliveryAddress: true,
    },
    Questionnaires: [
      {
        sectionName: "General",
        totalWeightage: 25,
        questions: [
          {
            description: "Is your company registered with CIDB as a specialist MEP contractor?",
            type: "Yes/No",
            mandatory: true,
            weightage: 8,
          },
          {
            description: "Provide your CIDB registration grade and specialization category.",
            type: "Short Text",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Describe your past experience in MEP installation for residential high-rise projects.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
          },
        ],
      },
      {
        sectionName: "Commercial",
        totalWeightage: 25,
        questions: [
          {
            description: "Are you able to meet the progress-based (monthly valuation) payment terms?",
            type: "Yes/No",
            mandatory: true,
            weightage: 10,
          },
          {
            description: "Provide your detailed cost breakdown for plumbing and fire protection works.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
          },
          {
            description: "Do you include material cost in your quoted sub-contract price?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["Yes - Labour & Material", "Labour Only", "Separate Quotation"],
          },
        ],
      },
      {
        sectionName: "Technical",
        totalWeightage: 15,
        questions: [
          {
            description: "Describe your approach for coordinating MEP works with the main contractor's construction schedule.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Do your plumbers and welders hold valid SKM or IWS certifications?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "How many residential high-rise MEP projects have you completed?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["1-5", "6-10", "11-20", "More than 20"],
          },
        ],
      },
      {
        sectionName: "Legal",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you accept the back-to-back sub-contract terms and conditions?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Are there any pending legal disputes or claims against your company?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Confirm you will provide a 5% performance bond upon award of sub-contract.",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
        ],
      },
      {
        sectionName: "Documents",
        totalWeightage: 20,
        questions: [
          {
            description: "Upload CIDB registration certificate and specialist license.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Upload BOMBA approval letters from previous fire protection projects.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Upload company profile with key personnel CVs and project references.",
            type: "File Upload",
            mandatory: true,
            weightage: 6,
          },
        ],
      },
    ],
    customerTermsAndConditions: [
      "Works must comply with UBBL 1984, MS 1064, and BOMBA regulations",
      "Defect liability period of 24 months from practical completion",
    ],
    supplierTermsAndConditions: [
      "Monthly progress claims subject to joint site measurement with main contractor",
      "Variation orders must be approved in writing before execution",
    ],
  },
  {
    tenderID: "MCMC-2025-CNS-006",
    tenderTitle: "Construction of Satellite Communication Station",
    customerName: "MCMC Satellite Division",
    requestedBy: "Ir. Roslan Bakar",
    paymentTerms: "Progress-based payments",
    isNegotiable: true,
    validateDate: "2025-10-01",
    tenderType: "Restricted",
    category: "Materials",
    status: "Not Interested",
    site: {
      projectName: "East Malaysia Satellite Uplink",
      siteCode: "MCMC-SATUP-01",
    },
    submissionDeadline: "2025-10-20",
    expressionOfInterestDeadline: "2025-09-25",
    description:
      "Civil and structural works for establishing satellite uplink and monitoring facility in Labuan.",
    materialsOrServices: [
      {
        lineNo: 8,
        itemType: "Material",
        category: "Construction",
        materialName: "Precast Concrete Foundations",
        quantity: 10,
        unitPrice: 20000,
        uom: "Units",
        availableStock: 0,
        needByDate: "2025-11-10",
        billingAddress: "MCMC Infra Projects, Cyberjaya",
        deliveryAddress: "Satellite Facility Grounds, Labuan",
        description:
          "Custom-designed, reinforced base structures for satellite antennas.",
        attachments: [],
      },
    ],
    deliverySettings: {
      isLineLevelNeedByDate: true,
      isLineLevelDeliveryAddress: true,
    },
    Questionnaires: [
      {
        sectionName: "General",
        totalWeightage: 25,
        questions: [
          {
            description: "Have you completed projects for Malaysian government agencies?",
            type: "Yes/No",
            mandatory: true,
            weightage: 8,
          },
          {
            description: "Provide company registration and CIDB grade details.",
            type: "Short Text",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Describe your organization's construction capabilities.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
          },
        ],
      },
      {
        sectionName: "Commercial",
        totalWeightage: 25,
        questions: [
          {
            description: "Are you able to meet the progress-based payment terms?",
            type: "Yes/No",
            mandatory: true,
            weightage: 10,
          },
          {
            description: "Provide your cost estimation for satellite station construction.",
            type: "Long Text",
            mandatory: true,
            weightage: 10,
          },
          {
            description: "Do you provide performance bonds for construction projects?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["Yes - Bank Guarantee", "Yes - Insurance Bond", "No"],
          },
        ],
      },
      {
        sectionName: "Technical",
        totalWeightage: 15,
        questions: [
          {
            description: "Briefly explain your construction QA/QC approach.",
            type: "Long Text",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Do you have experience with satellite facility construction?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "What is your CIDB registration grade?",
            type: "Single Choice",
            mandatory: true,
            weightage: 5,
            options: ["G1-G3", "G4-G5", "G6", "G7"],
          },
        ],
      },
      {
        sectionName: "Legal",
        totalWeightage: 15,
        questions: [
          {
            description: "Do you accept the standard terms and conditions?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Are there any pending legal disputes against your company?",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
          {
            description: "Confirm all permits and insurance will be covered by contractor.",
            type: "Yes/No",
            mandatory: true,
            weightage: 5,
          },
        ],
      },
      {
        sectionName: "Documents",
        totalWeightage: 20,
        questions: [
          {
            description: "Attach CIDB certification and project list.",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Upload company registration certificate (SSM).",
            type: "File Upload",
            mandatory: true,
            weightage: 7,
          },
          {
            description: "Upload insurance and permit documentation.",
            type: "File Upload",
            mandatory: true,
            weightage: 6,
          },
        ],
      },
    ],
    customerTermsAndConditions: [
      "Site access coordination with MCMC and PBT is required",
    ],
    supplierTermsAndConditions: [
      "Contractor to ensure all permits and insurance are covered",
    ],
    notInterestedReason: "because of some reason ",
  },
];

const initialState: TenderState = {
  tenders: tenders,
  loading: false,
  error: null,
};

// Example async thunk for fetching tenders
export const fetchTenders = createAsyncThunk<Tender[]>(
  "tenders/fetch",
  async () => {
    const res = await fetch("/api/tenders");
    if (!res.ok) throw new Error("Failed to fetch tenders");
    return res.json();
  }
);

export const tenderSlice = createSlice({
  name: "tenders",
  initialState,
  reducers: {
    setTenders(state, action: PayloadAction<Tender[]>) {
      state.tenders = action.payload;
    },
    updateTender(state, action: PayloadAction<Partial<Tender>>) {
      const index = state.tenders.findIndex(
        (tender) => tender.tenderID === action.payload.tenderID
      );
      if (index !== -1) {
        state.tenders[index] = { ...state.tenders[index], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTenders.fulfilled,
        (state, action: PayloadAction<Tender[]>) => {
          state.loading = false;
          state.tenders = action.payload;
        }
      )
      .addCase(fetchTenders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Something went wrong";
      });
  },
});

export const { setTenders, updateTender } = tenderSlice.actions;
export default tenderSlice.reducer;
